import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { fetchPendingActions } from "../containers/FieldApp/fieldAppApi";

const PendingActionsContext = createContext({
  pendingCount: 0,
  pendingItems: [],
  loading: false,
  error: null,
  refreshPendingActions: () => {},
  setPendingActionsEditing: () => {},
});

export function usePendingActions() {
  return useContext(PendingActionsContext);
}

export function PendingActionsProvider({ children }) {
  const [pendingCount, setPendingCount] = useState(0);
  const [pendingItems, setPendingItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const editingCountRef = useRef(0);

  const refreshPendingActions = useCallback(async ({ background = false } = {}) => {
    if (!background) {
      setLoading(true);
    }
    setError(null);

    try {
      const data = await fetchPendingActions();
      const items = Array.isArray(data.items) ? data.items : [];
      setPendingItems(items);
      setPendingCount(
        typeof data.count === "number" ? data.count : items.length
      );
    } catch (err) {
      setError(err);
    } finally {
      if (!background) {
        setLoading(false);
      }
    }
  }, []);

  const setPendingActionsEditing = useCallback((isEditing) => {
    editingCountRef.current = Math.max(
      0,
      editingCountRef.current + (isEditing ? 1 : -1)
    );
  }, []);

  useEffect(() => {
    refreshPendingActions();
    const interval = setInterval(() => {
      if (editingCountRef.current > 0) {
        return;
      }
      refreshPendingActions({ background: true });
    }, 20000);
    return () => clearInterval(interval);
  }, [refreshPendingActions]);

  return (
    <PendingActionsContext.Provider
      value={{
        pendingCount,
        pendingItems,
        loading,
        error,
        refreshPendingActions,
        setPendingActionsEditing,
      }}
    >
      {children}
    </PendingActionsContext.Provider>
  );
}
